/**
 * One definition of what a user status looks like, and what it is called.
 *
 * There were two, and they disagreed in a way that mattered. `Users.tsx` was a
 * binary ternary -- green for Active, red for everything else -- and
 * `Doctors.tsx` was a lookup that returned `undefined` for anything it did not
 * name. So a deleted account was drawn bright red on one page and in the
 * default body colour on the other, and a provider on a payment hold was red on
 * both, identically to one an administrator had banned.
 *
 * ## Why Deleted is grey and not red
 *
 * Red in this console means "we stopped them". Nobody stopped a deleted
 * account: the person asked to leave and we did as they asked. Colouring that
 * as a sanction misreads the one fact the row is there to record, and it puts
 * the loudest colour on the screen next to the rows an operator can do least
 * about.
 *
 * Amber for a payment hold, because that one is temporary and reversible by the
 * provider themselves -- it is the only status on the list that is waiting for
 * somebody to do something.
 */
export const USER_STATUS_COLOURS: Record<string, string> = {
  Active: '#4BA16E',
  Blocked: '#F05763',
  'Not verified': '#00BEF2',
  'Payment hold': '#B58A00',
  Deleted: '#6A737D',
};

/** The body colour, for a status this console has not been taught yet. */
const UNKNOWN_STATUS_COLOUR = '#3C3C60';

export const statusColour = (status?: string | null): string =>
  (status && USER_STATUS_COLOURS[status]) || UNKNOWN_STATUS_COLOUR;

/**
 * Every status the API can return, in the order an operator thinks about them.
 *
 * Used to populate the status dropdowns. `Deleted`, `Payment hold` and
 * `Not verified` are on this list because a `<Select>` whose value matches none
 * of its `<MenuItem>`s renders BLANK -- so a deleted account's detail page
 * showed an empty status box, which reads as "we do not know" rather than as
 * the one thing about that account that is certain.
 */
export const USER_STATUSES = [
  'Active',
  'Blocked',
  'Not verified',
  'Payment hold',
  'Deleted',
] as const;

/**
 * Statuses an administrator may WRITE from the detail page.
 *
 * The other three are OUTCOMES, each owned by the code path that produces it,
 * and each carrying work that a bare status write skips:
 *
 *   - `Not verified` is cleared by the provider approval route, which records
 *     who decided and when and notifies the provider.
 *   - `Payment hold` is applied by billing against an unpaid invoice and
 *     released when a payment clears.
 *   - `Deleted` is the tombstone `deleteAccount` leaves behind, after the dues
 *     and live-visit gates, the anonymisation and the closure record.
 *
 * Mirrors `ADMIN_WRITABLE_STATUSES` in the API
 * (Backend/src/module/user/user.service.ts). That one is the rule; this one is
 * the affordance. Until the API grew its copy, the comment here claimed a
 * server-side refusal that did not exist -- only the deleted-SUBJECT case was
 * ever guarded, and an admin token could PATCH any of the three straight onto
 * the column. Keep the two lists in step.
 */
export const ADMIN_WRITABLE_STATUSES = ['Active', 'Blocked'] as const;

/** True when the status is not one an administrator may write. */
export const isReadOnlyStatus = (status?: string | null): boolean =>
  !!status && !(ADMIN_WRITABLE_STATUSES as readonly string[]).includes(status);

/**
 * True when the account, as it stands, accepts no status change at all.
 *
 * Only a closed one. This used to be `isReadOnlyStatus(currentStatus)`, which
 * conflated two different questions -- "is this status mine to write?" with "is
 * this SUBJECT writable?" -- and so disabled the whole control for a provider
 * on a payment hold. The server has no objection to banning a suspended
 * provider, and an administrator who needs to stop one working should not have
 * to settle their invoice first; there was simply no way to do it.
 *
 * A deleted account is genuinely different: `updateNew` refuses every edit to
 * one with ACCOUNT_ALREADY_DELETED, so the control could only ever produce an
 * error message.
 */
export const isClosedAccount = (status?: string | null): boolean =>
  status === 'Deleted';

/**
 * Whether an administrator may move an account from `from` to `to`.
 *
 * Beyond the writable set there is one transition this console greys out:
 * `Payment hold` -> `Active`. Lifting a billing suspension by hand would put a
 * provider back to work still owing, and leave billing's view of them
 * disagreeing with the column. Settling or writing off the invoice on the
 * Finance page releases the hold as a consequence, which is the route the
 * refusal names.
 *
 * `Payment hold` -> `Blocked` stays available on purpose: a ban is orthogonal
 * to money.
 *
 * ## This is an approximation, and the server is the rule
 *
 * The API refuses `Active` for any provider with an issued unpaid invoice,
 * whatever status they are currently on -- because the status is washable and
 * the debt is not, and testing the status alone was defeated by
 * `Payment hold` -> `Blocked` -> `Active`. This console cannot make that test:
 * it does not know what is owed without a second request, and the status is
 * the only signal on the page.
 *
 * So the greying catches the obvious case and the server catches the rest. A
 * `Blocked` provider who still owes money will be offered `Active` here and
 * refused, and the refusal now lands in the message under the dropdown with
 * the Finance page named -- which is the whole reason that error surface
 * exists. Guessing harder client-side would only add a way to be wrong.
 */
export const canWriteStatus = (
  from: string | null | undefined,
  to: string,
): boolean => {
  if (isReadOnlyStatus(to)) return false;
  if (isClosedAccount(from)) return false;
  if (from === 'Payment hold' && to === 'Active') return false;
  return true;
};

/**
 * Why a particular option is greyed, for the operator rather than the log.
 *
 * Role-aware, because half of these are meaningless to a patient: patients are
 * never reviewed and never put on a payment hold -- `signUp` sets NOT_VERIFIED
 * only for a provider, and billing invoices only providers -- so telling an
 * operator on a patient's page that a status is "cleared by Approve or Reject"
 * describes machinery that does not exist for that account.
 */
export const statusOptionHint = (
  from: string | null | undefined,
  to: string,
  role?: string | null,
): string => {
  const isProvider = role === 'Doctor/Nurse';

  if (from === 'Payment hold' && to === 'Active') {
    return 'Settle or write off their invoice on the Finance page and the suspension lifts by itself.';
  }
  if (to === 'Deleted') {
    return 'Use Close account at the foot of this page.';
  }
  if (!isProvider) {
    // The remaining two are provider-only states. A patient can never be in
    // one, so the honest answer is to say nothing rather than to explain a
    // process that will never run for them.
    return '';
  }
  if (to === 'Not verified') {
    return 'Set at registration and cleared by Approve or Reject.';
  }
  if (to === 'Payment hold') {
    return 'Applied by billing when dues go unpaid.';
  }
  return '';
};
